/**
 * Date / period helpers for the Eva usage views.
 *
 * sf-api's read endpoints accept either an explicit "YYYY-MM" period (plus the
 * keywords "this-month" / "last-month") for totals, or a from/to "YYYY-MM-DD"
 * range with a granularity for series. These helpers centralize the UTC math so
 * every Eva page derives ranges the same way.
 */

/** Number of trailing days shown on the daily trend chart. */
export const DAILY_TREND_DAYS = 30;

/** Number of trailing months shown on the monthly trend chart. */
export const MONTHLY_TREND_MONTHS = 12;

/** Series granularities accepted by the sf-api series endpoints. */
export type Granularity = 'daily' | 'monthly';

/** Format a Date as "YYYY-MM-DD" in UTC. */
export function toDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Format a Date as "YYYY-MM" in UTC. */
export function toPeriodString(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** The current calendar month as "YYYY-MM" (UTC). */
export function currentPeriod(): string {
  return toPeriodString(new Date());
}

/** The previous calendar month as "YYYY-MM" (UTC). */
export function previousPeriod(): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return toPeriodString(d);
}

/**
 * Inclusive from/to range for the daily trend: the last `DAILY_TREND_DAYS`
 * days ending today (UTC).
 */
export function dailyTrendRange(today: Date = new Date()): { from: string; to: string } {
  const to = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (DAILY_TREND_DAYS - 1));
  return { from: toDateString(from), to: toDateString(to) };
}

/**
 * Inclusive from/to range for the current calendar month: from the first day of
 * today's UTC month through today. Unlike `dailyTrendRange` (a rolling window
 * that straddles the month boundary), this aligns the daily trend chart with the
 * month-to-date KPI tiles so the two reconcile.
 */
export function currentMonthRange(today: Date = new Date()): { from: string; to: string } {
  const from = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const to = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  return { from: toDateString(from), to: toDateString(to) };
}

/**
 * Inclusive from/to range for the monthly trend: the first day of the month
 * `MONTHLY_TREND_MONTHS - 1` months ago through today (UTC). The backend buckets
 * by month, so any day within the start/end months is sufficient.
 */
export function monthlyTrendRange(today: Date = new Date()): { from: string; to: string } {
  const to = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const from = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - (MONTHLY_TREND_MONTHS - 1), 1),
  );
  return { from: toDateString(from), to: toDateString(to) };
}

/**
 * Earliest selectable period for the Eva company-detail period selector. Eva
 * usage rollups only exist from May 2026 onward, so the month picker is clamped
 * to this floor (SF-3834).
 */
export const MIN_SELECTABLE_PERIOD = '2026-05';

/**
 * The list of selectable "YYYY-MM" periods for the period selector, from
 * {@link MIN_SELECTABLE_PERIOD} (May 2026) through the current period inclusive,
 * ordered DESCENDING (newest first) so the current month is the default top
 * option. UTC-based throughout. When the current period is at or before the
 * floor the list collapses to just the current period (never empty — the start
 * is clamped to the earlier of the floor / current period).
 *
 * @param today - the reference "now" (defaults to the current date); used so the
 *   upper bound advances as the clock crosses a month boundary
 * @returns array of "YYYY-MM" strings, newest first
 */
export function monthOptions(today: Date = new Date()): string[] {
  const current = toPeriodString(today);
  // Pre-launch / mocked dates can put "now" before the floor. In that case the
  // selector must still offer the current period (deterministic, non-empty by
  // contract) rather than collapsing to an empty list — clamp the walk's start
  // to whichever of the floor / current period is EARLIER.
  const start = current < MIN_SELECTABLE_PERIOD ? current : MIN_SELECTABLE_PERIOD;
  const [startYear, startMonth] = start.split('-').map(Number);
  // Walk forward from the start to the current month (inclusive), accumulating
  // each "YYYY-MM", then reverse so the newest period sits first.
  const options: string[] = [];
  let cursor = new Date(Date.UTC(startYear, startMonth - 1, 1));
  let period = toPeriodString(cursor);
  while (period <= current) {
    options.push(period);
    cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
    period = toPeriodString(cursor);
  }
  return options.reverse();
}

/**
 * Inclusive from/to range for a selected "YYYY-MM" period (UTC). `from` is the
 * first day of the month. `to` clamps to today when the period is the current
 * month (so the daily chart never plots empty future days), otherwise it spans
 * the full month through its last day. Mirrors {@link currentMonthRange} for the
 * current month while supporting arbitrary past months (SF-3834).
 *
 * @param period - the "YYYY-MM" period to range over
 * @param today - the reference "now" (defaults to the current date)
 * @returns inclusive { from, to } "YYYY-MM-DD" range
 */
export function monthRange(period: string, today: Date = new Date()): { from: string; to: string } {
  const [year, month] = period.split('-').map(Number);
  const from = new Date(Date.UTC(year, month - 1, 1));
  const isCurrent = period === toPeriodString(today);
  const to = isCurrent
    ? new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()))
    : // Day 0 of the next month is the last day of this month (UTC).
      new Date(Date.UTC(year, month, 0));
  return { from: toDateString(from), to: toDateString(to) };
}

/**
 * The "YYYY-MM" period one calendar month before the given period (UTC). Used to
 * drive the "Last-Month Spend" tile relative to the SELECTED month rather than
 * always the actual previous month (SF-3834).
 *
 * @param period - the reference "YYYY-MM" period
 * @returns the prior month as "YYYY-MM"
 */
export function priorPeriod(period: string): string {
  const [year, month] = period.split('-').map(Number);
  return toPeriodString(new Date(Date.UTC(year, month - 2, 1)));
}

/**
 * Human-friendly label for a series bucket key.
 * Daily buckets are "YYYY-MM-DD" → "DD MMM"; monthly buckets are "YYYY-MM" →
 * "MMM YYYY". Unrecognized keys pass through unchanged.
 */
export function formatBucketLabel(bucket: string, granularity: Granularity): string {
  if (granularity === 'daily' && /^\d{4}-\d{2}-\d{2}$/.test(bucket)) {
    const d = new Date(`${bucket}T00:00:00.000Z`);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', timeZone: 'UTC' });
  }
  if (granularity === 'monthly' && /^\d{4}-\d{2}$/.test(bucket)) {
    const d = new Date(`${bucket}-01T00:00:00.000Z`);
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  return bucket;
}

/**
 * Format an ISO timestamp as a short "DD MMM YYYY" date for display (e.g. a
 * last-used date). Null/undefined/unparseable values render as an em-dash so
 * missing data never shows a misleading "Invalid Date".
 * @param iso - ISO-8601 timestamp string, or null/undefined
 * @returns localized short date, or em-dash when absent/invalid
 */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '\u2014';
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
